import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, ListGroup, Button, Form, Badge, Alert, Tabs, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faSearch, faUser, faRobot, faClock, faCode, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faTelegram } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { API_URL } from '../config';

// Add a utility function to log responses in detail
const logResponseDetails = (response, label = "Response data") => {
  console.group(`📝 ${label}`);
  console.log('Status:', response.status);
  console.log('Headers:', response.headers);
  console.log('Data:', response.data);
  console.groupEnd();
  
  return response;
};

// Add an interceptor to log all responses in development mode
axios.interceptors.response.use(
  (response) => {
    if (response.config.url.includes('/messages')) {
      console.log(`✅ ${response.config.method.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  (error) => {
    console.error(`❌ ERROR ${error.config?.method?.toUpperCase() || 'REQUEST'} ${error.config?.url || 'unknown'} - ${error.response?.status || 'unknown'}`);
    console.error('Error response:', error.response?.data);
    return Promise.reject(error);
  }
);

function Messages() {
  const [isLoading, setIsLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [withinMessagingWindow, setWithinMessagingWindow] = useState(true);
  const [remainingTime, setRemainingTime] = useState(null);
  const [manualResponse, setManualResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [configuredChannels, setConfiguredChannels] = useState({
    whatsapp: false,
    telegram: false
  });

  useEffect(() => {
    fetchConversations();
    fetchUserChannelConfig();
  }, []);

  const fetchUserChannelConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/channels/user`);
      const userChannels = response.data.userChannels || {};
      
      setConfiguredChannels({
        whatsapp: !!userChannels.WhatsApp,
        telegram: !!userChannels.Telegram
      });
      
      console.log('Configured channels:', userChannels);
    } catch (err) {
      console.error('Error fetching user channel configuration:', err);
    }
  };

  const fetchConversations = async () => {
    try {
      setIsLoading(true);
      console.log('📤 Fetching conversations...');
      const response = await axios.get(`${API_URL}/messages`);
      logResponseDetails(response, "Conversations data");
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Effect to scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Polling for real-time updates when a conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      // Poll for new messages every 3 seconds
      const interval = setInterval(async () => {
        try {
          console.log(`📤 Polling messages for ${selectedConversation.sender_id}...`);
          const response = await axios.get(`${API_URL}/messages/${selectedConversation.sender_id}`);
          
          // Only update if there are new messages
          if (response.data.messages.length !== messages.length) {
            console.log(`📥 New messages received: ${response.data.messages.length - messages.length}`);
            logResponseDetails(response, "Updated messages data");
            setMessages(response.data.messages);
            setWithinMessagingWindow(response.data.withinMessagingWindow);
            setRemainingTime(response.data.remainingTime);
            
            // Also refresh conversations list to update last message
            fetchConversations();
          } else {
            console.log('📥 No new messages');
          }
        } catch (err) {
          console.error('Error polling for new messages:', err);
        }
      }, 3000); // Poll every 3 seconds

      setPollingInterval(interval);

      // Cleanup interval when conversation changes or component unmounts
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Clear polling when no conversation is selected
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [selectedConversation?.sender_id, messages.length]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const selectConversation = async (conversation) => {
    try {
      setSelectedConversation(conversation);
      console.log(`📤 Loading conversation for ${conversation.sender_id}...`);
      const response = await axios.get(`${API_URL}/messages/${conversation.sender_id}`);
      logResponseDetails(response, "Conversation details");
      
      setMessages(response.data.messages);
      setWithinMessagingWindow(response.data.withinMessagingWindow);
      setRemainingTime(response.data.remainingTime);
      
      // Auto-scroll to bottom after loading messages
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError('Failed to load conversation');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!manualResponse.trim() || !selectedConversation) return;
    
    try {
      setIsSubmitting(true);
      setError(''); // Clear any previous errors
      
      console.log(`📤 SENDING MESSAGE - Details:`);
      console.group('Message Request Data');
      console.log('To:', selectedConversation.sender_id);
      console.log('Channel:', selectedConversation.channel_name);
      console.log('Content:', manualResponse);
      console.groupEnd();
      
      const requestData = {
        senderId: selectedConversation.sender_id,
        channelId: selectedConversation.channel_id,
        manualResponse,
        response: manualResponse, // Add both parameters to ensure one works
        is_manual_response: true,
        debug: true,
        log_level: 'debug' // Request detailed logging
      };
      
      // Add debugging statement to show exactly what's being sent
      console.log('📦 Request payload:', JSON.stringify(requestData, null, 2));
      
      const response = await axios.post(`${API_URL}/messages/send`, requestData)
        .then(resp => logResponseDetails(resp, "Message send response"));
      
      console.log('📊 Message status:', response.data.success ? 'Sent successfully' : 'Failed to send');
      
      if (response.data.warning) {
        setError(`Message saved but not sent: ${response.data.warning}`);
      }
      
      console.log('📤 Refreshing conversation after send...');
      
      // Refresh conversation after sending
      const refreshResponse = await axios.get(`${API_URL}/messages/${selectedConversation.sender_id}`)
        .then(resp => logResponseDetails(resp, "Conversation refresh data"));
        
      setMessages(refreshResponse.data.messages);
      setWithinMessagingWindow(refreshResponse.data.withinMessagingWindow);
      setRemainingTime(refreshResponse.data.remainingTime);
      
      // Clear input
      setManualResponse('');
      
      // Refresh conversations list to update last message
      fetchConversations();
    } catch (err) {
      console.error('Error sending message:', err);
      console.error('Error details:', err.response ? JSON.stringify(err.response.data, null, 2) : 'No response data');
      setError(err.response?.data?.message || `Failed to send message: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    if (cleaned.length > 10) {
      return '+' + cleaned.substr(0, 2) + ' ' + 
             cleaned.substr(2, 3) + ' ' + 
             cleaned.substr(5, 3) + ' ' + 
             cleaned.substr(8);
    }
    return phoneNumber;
  };

  // Time ago formatter
  const timeAgo = (timestamp) => {
    const now = new Date();
    const messageDate = new Date(timestamp);
    const diff = Math.floor((now - messageDate) / 1000);
    
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return 'Yesterday';
    return messageDate.toLocaleDateString();
  };

  // Filter conversations based on search AND active tab
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.sender_id.toLowerCase().includes(searchTerm.toLowerCase());
    // If tab is 'all', show all conversations that match search
    // Otherwise, filter by channel_name that matches the tab
    const matchesTab = activeTab === 'all' || 
                      (activeTab === 'whatsapp' && conv.channel_name === 'WhatsApp') ||
                      (activeTab === 'telegram' && conv.channel_name === 'Telegram');
    
    return matchesSearch && matchesTab;
  });

  // Check if there are any WhatsApp conversations
  const hasWhatsAppConversations = conversations.some(conv => conv.channel_name === 'WhatsApp');
  
  // Check if there are any Telegram conversations
  const hasTelegramConversations = conversations.some(conv => conv.channel_name === 'Telegram');

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <Row className="h-100 g-0">
        {/* Conversations List - Left Panel */}
        <Col md={4} className="chat-sidebar">
          <Card className="h-100 border-0 rounded-0">
            <Card.Header className="chat-sidebar-header">
              <h5 className="m-0 fw-bold">Messages</h5>
              <div className="mt-2">
                <Form.Control
                  type="text"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="chat-search"
                />
              </div>
              
              {/* Channel tabs navigation */}
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mt-3"
                fill
              >
                <Tab 
                  eventKey="all" 
                  title={
                    <span>
                      <FontAwesomeIcon icon={faComments} className="me-2" />
                      All
                    </span>
                  }
                />
                {(hasWhatsAppConversations || configuredChannels.whatsapp) && (
                  <Tab 
                    eventKey="whatsapp" 
                    title={
                      <span>
                        <FontAwesomeIcon icon={faWhatsapp} className="me-2 text-success" />
                        WhatsApp
                      </span>
                    }
                  />
                )}
                {(hasTelegramConversations || configuredChannels.telegram) && (
                  <Tab 
                    eventKey="telegram" 
                    title={
                      <span>
                        <FontAwesomeIcon icon={faTelegram} className="me-2 text-primary" />
                        Telegram
                      </span>
                    }
                  />
                )}
              </Tabs>
            </Card.Header>
            <Card.Body className="p-0">
              {filteredConversations.length === 0 ? (
                <div className="empty-conversations">
                  <FontAwesomeIcon icon={faComments} size="3x" className="mb-3 text-muted" />
                  <h6>No conversations yet</h6>
                  <p className="text-muted">
                    {activeTab === 'all' 
                      ? 'Messages from your customers will appear here' 
                      : activeTab === 'whatsapp'
                        ? 'No WhatsApp conversations available'
                        : 'No Telegram conversations available'}
                  </p>
                </div>
              ) : (
                <div className="conversations-list">
                  {filteredConversations.map((conversation) => {
                    const lastMessagePreview = conversation.last_message?.length > 40 
                      ? conversation.last_message.substring(0, 40) + '...' 
                      : conversation.last_message;
                    
                    return (
                      <div 
                        key={conversation.sender_id} 
                        className={`conversation-item ${selectedConversation?.sender_id === conversation.sender_id ? 'active' : ''}`}
                        onClick={() => selectConversation(conversation)}
                      >
                        <div className="conversation-avatar">
                          {conversation.channel_name === 'WhatsApp' ? (
                            <FontAwesomeIcon icon={faWhatsapp} className="text-success" />
                          ) : (
                            <FontAwesomeIcon icon={faTelegram} className="text-primary" />
                          )}
                        </div>
                        <div className="conversation-content">
                          <div className="conversation-header">
                            <h6 className="conversation-name">
                              {formatPhoneNumber(conversation.sender_id)}
                            </h6>
                            <small className="conversation-time">
                              {timeAgo(conversation.last_message_time)}
                            </small>
                          </div>
                          <div className="conversation-preview">
                            <p className="last-message">{lastMessagePreview}</p>
                            <Badge bg="light" text="dark" className="message-count">
                              {conversation.message_count}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Chat Panel - Right Panel */}
        <Col md={8} className="chat-main">
          {selectedConversation ? (
            <Card className="h-100 border-0 rounded-0">
              {/* Chat Header */}
              <Card.Header className="chat-header">
                <div className="d-flex align-items-center">
                  <div className="chat-avatar me-3">
                    {selectedConversation.channel_name === 'WhatsApp' ? (
                      <FontAwesomeIcon icon={faWhatsapp} className="text-success" />
                    ) : (
                      <FontAwesomeIcon icon={faTelegram} className="text-primary" />
                    )}
                  </div>
                  <div className="chat-info">
                    <h6 className="m-0 fw-bold">
                      {formatPhoneNumber(selectedConversation.sender_id)}
                    </h6>
                    <small className="text-muted">
                      {selectedConversation.channel_name}
                      {selectedConversation.channel_name === 'WhatsApp' && withinMessagingWindow && remainingTime && (
                        <Badge bg="success" className="ms-2">
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          {remainingTime.hours}h {remainingTime.minutes}m left
                        </Badge>
                      )}
                    </small>
                  </div>
                </div>
              </Card.Header>

              {/* Messages Area */}
              <Card.Body className="chat-messages-container">
                <div className="chat-messages">
                  {messages.map((message) => (
                    <React.Fragment key={message.id}>
                      {/* Incoming Message */}
                      {message.message && (
                        <div className="message-wrapper incoming">
                          <div className="message-bubble incoming">
                            <div className="message-text">
                              {message.message.split('\n').map((text, i) => (
                                <React.Fragment key={i}>
                                  {text}
                                  {i !== message.message.split('\n').length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </div>
                            <div className="message-time">
                              {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Outgoing Message */}
                      {message.response && (
                        <div className="message-wrapper outgoing">
                          <div className={`message-bubble outgoing ${message.is_manual_response ? 'manual' : 'ai'}`}>
                            <div className="message-text">
                              {message.response.split('\n').map((text, i) => (
                                <React.Fragment key={i}>
                                  {text}
                                  {i !== message.response.split('\n').length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </div>
                            <div className="message-meta">
                              <span className="message-time">
                                {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                              <Badge 
                                bg={message.is_manual_response ? 'success' : 'info'} 
                                className="message-type ms-2"
                              >
                                <FontAwesomeIcon 
                                  icon={message.is_manual_response ? faUser : faRobot} 
                                  className="me-1" 
                                />
                                {message.is_manual_response ? 'You' : 'AI'}
                              </Badge>
                              {message.mcp_calls && (
                                <Button variant="outline-secondary" size="sm" className="ms-2">
                                  <FontAwesomeIcon icon={faCode} className="me-1" />
                                  API
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  {/* Add ref for auto-scrolling */}
                  <div ref={messagesEndRef} />
                </div>
              </Card.Body>

              {/* Message Input */}
              <Card.Footer className="chat-input-container">
                {(selectedConversation.channel_name !== 'WhatsApp' || withinMessagingWindow) ? (
                  <Form onSubmit={handleSendMessage} className="chat-input-form">
                    <div className="input-group">
                      <Form.Control
                        as="textarea"
                        rows={1}
                        value={manualResponse}
                        onChange={(e) => setManualResponse(e.target.value)}
                        placeholder="Type your message..."
                        className="chat-input"
                        style={{ resize: 'none' }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                      />
                      <Button 
                        variant="primary" 
                        type="submit" 
                        disabled={isSubmitting || !manualResponse.trim()}
                        className="send-button"
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                      </Button>
                    </div>
                    {selectedConversation.channel_name === 'WhatsApp' && remainingTime && (
                      <small className="text-muted d-block mt-1">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        Messaging window: {remainingTime.hours}h {remainingTime.minutes}m remaining
                      </small>
                    )}
                  </Form>
                ) : (
                  <Alert variant="warning" className="m-0">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faClock} className="me-2" />
                      <div>
                        <strong>24-hour messaging window expired</strong>
                        <br />
                        <small>Wait for customer to message or use approved templates</small>
                      </div>
                    </div>
                  </Alert>
                )}
              </Card.Footer>
            </Card>
          ) : (
            <div className="chat-placeholder">
              <div className="text-center">
                <FontAwesomeIcon icon={faComments} size="4x" className="mb-4 text-muted" />
                <h4>Select a conversation</h4>
                <p className="text-muted">Choose a conversation from the left to start chatting</p>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default Messages;
